import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Button, Checkbox, FormControl, FormLabel, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Stack, Text, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { RecurringRevenue, HELP_DESK_TIER_LABEL } from '../models/recurringRevenue';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface Props {
  recurring: RecurringRevenue | undefined;
  isOpen: boolean;
  onClose: () => void;
}

// item 1360 (Chad) — WHAT this subscription delivers, ticked and VERIFIED by a human. SKUs pre-fill
// these when the engine minted the sub; a manual add (loose SKUs) gets fixed right here. Feeds the
// RNWY entitlements API — an unverified/untagged sub reads to RNWY as "unknown", never as "no".
const RecurringProductsModal: React.FC<Props> = ({ recurring, isOpen, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rnwy, setRnwy] = useState(false);
  const [sp, setSp] = useState(false);
  const [hd, setHd] = useState(false);
  const [tier, setTier] = useState(0);
  const [tp, setTp] = useState(false);
  const [verified, setVerified] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !recurring) return;
    setRnwy(!!recurring.isRnwy);
    setSp(!!recurring.isSwitchPilot);
    setHd(!!recurring.isHelpDesk);
    setTier(recurring.helpDeskTier ?? 0);
    setTp(!!recurring.isThirdParty);
    setVerified(!!recurring.productsVerifiedDate);
  }, [isOpen, recurring]);

  const none = !rnwy && !sp && !hd && !tp;
  const tierMissing = hd && !(tier >= 1 && tier <= 3);

  const save = async () => {
    if (!recurring) return;
    setSaving(true);
    try {
      await axios.post(`${apiUrl()}RecurringRevenue/${recurring.id}/products`, {
        isRnwy: rnwy, isSwitchPilot: sp, isHelpDesk: hd, helpDeskTier: hd ? tier : 0, isThirdParty: tp, verified,
      });
      queryClient.invalidateQueries('recurringRevenue');
      toast({ status: 'success', title: `${recurring.name} — products ${verified ? 'verified' : 'saved'}.` });
      onClose();
    } catch (e: any) {
      // item 1360c — say WHAT failed (status + server text, or the network/client error); the
      // first version swallowed an empty body into a blank toast.
      const status = e?.response?.status;
      const data = e?.response?.data;
      let serverText = '';
      if (typeof data === 'string') serverText = data;
      else if (data) serverText = JSON.stringify(data);
      const reason = [status ? `HTTP ${status}` : null, serverText || e?.message || null].filter(Boolean).join(' — ') || 'Try again.';
      // eslint-disable-next-line no-console
      console.error('[products] save failed', {
        status, data, message: e?.message, url: `${apiUrl()}RecurringRevenue/${recurring.id}/products`,
      });
      toast({
        status: 'error', title: 'Could not save products', description: reason, duration: 12000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Products —
          {recurring?.name ?? 'subscription'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.400" mb={3}>
            What does this subscription deliver? RNWY reads these flags to know what the property is entitled to.
            {recurring?.customerName ? ` · ${recurring.customerName}` : ''}
            {recurring?.projectName ? ` · ${recurring.projectName}` : ''}
          </Text>
          <Stack spacing={2}>
            <Checkbox isChecked={rnwy} onChange={(e) => setRnwy(e.target.checked)}>RNWY subscription</Checkbox>
            <Checkbox isChecked={sp} onChange={(e) => setSp(e.target.checked)}>SwitchPilot subscription</Checkbox>
            <Checkbox isChecked={hd} onChange={(e) => { setHd(e.target.checked); if (!e.target.checked) setTier(0); }}>Help Desk subscription</Checkbox>
            {hd && (
              <FormControl pl={6} isInvalid={tierMissing}>
                <FormLabel fontSize="sm">Help Desk tier</FormLabel>
                <Select size="sm" value={tier} onChange={(e) => setTier(Number(e.target.value))}>
                  <option value={0}>Pick a tier…</option>
                  {[1, 2, 3].map((t) => <option key={t} value={t}>{HELP_DESK_TIER_LABEL[t]}</option>)}
                </Select>
              </FormControl>
            )}
            <Checkbox isChecked={tp} onChange={(e) => setTp(e.target.checked)}>Third-party integration subscription</Checkbox>
            {/* item 1360d (Chad: "no option to tag one not RNWY") — an EXPLICIT "none of these":
                clears the four and marks verified, so RNWY reads it as known-with-nothing, not unknown. */}
            <Checkbox
              isChecked={none && verified}
              onChange={(e) => {
                if (e.target.checked) { setRnwy(false); setSp(false); setHd(false); setTier(0); setTp(false); setVerified(true); } else setVerified(false);
              }}
            >
              None of these — not an RNWY-related subscription
            </Checkbox>
          </Stack>
          {none && !verified && (
            <Alert status="warning" mt={3} fontSize="sm" borderRadius="md">
              <AlertIcon />
              Nothing ticked and not verified — RNWY will see this subscription as UNKNOWN. Tick a product, or “None of these”.
            </Alert>
          )}
          {none && verified && (
            <Alert status="info" mt={3} fontSize="sm" borderRadius="md">
              <AlertIcon />
              Verified as none — RNWY will see this property with no entitlements from this subscription.
            </Alert>
          )}
          <Checkbox mt={4} isChecked={verified} onChange={(e) => setVerified(e.target.checked)} fontWeight="semibold">
            I verified these products are correct
          </Checkbox>
          {recurring?.productsVerifiedDate && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {`Last verified ${new Date(recurring.productsVerifiedDate.endsWith('Z') ? recurring.productsVerifiedDate : `${recurring.productsVerifiedDate}Z`).toLocaleString()}`}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={save} isLoading={saving} isDisabled={tierMissing}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RecurringProductsModal;
